import React from 'react';
import Logger from '../../util/Logger';
import { connect } from 'react-redux';
import _ from 'lodash';
import './Authentication.css';
import { AuthMethod } from '../../models/AuthMethod';
import User from '../../models/User';
import { updateAuthMethod } from '../../actions/authentication';
import { Radio, RadioGroup } from 'react-radio-group';
import { showDialog } from '../../actions/dialog';
import { handleErrorWithDialog } from '../../util/Errors';
import Tenant from '../../models/Tenant';
import { PAGE_LINK_AUTH_LEARN_MORE, PAGE_LINK_SAML_EXPLAIN } from '../../constants';
import { InteractiveHelp } from '../../analytics/InteractiveHelp';
import SamlSettings from './SamlSettings';
import { EnterpriseSettings } from '../../analytics/EnterpriseSettings';
import { NavigationUtils } from '../../util/NavigationUtils';

class Authentication extends React.Component {

  /**
   * Called when either of the Radio buttons are selected
   */
  handleAuthMethodSelected = (selected) => {
    const { user, showDialog } = this.props;

    // The selected thing is just a string id
    const item = _.filter(getAuthMethods(), m => m.id === selected)[0];

    // If the tenant is a trial and trying to enable SAML, stop them
    if (Tenant.isTrial(User.getTenant(user)) && item.id === AuthMethod.LOGIN_WITH_SSO) {

      const handleTrialTenantContactClicked = () => InteractiveHelp.show();

      showDialog({
        title: 'Trial Tenants cannot use SSO',
        body: (
          <div>You cannot use SAML 2.0 Single Sign-On as a Trial customer.</div>
        ),
        ok: 'Contact US',
        cancel: 'Cancel',
        onOk: handleTrialTenantContactClicked
      });
    } else {
      switch (item.id) {
        case AuthMethod.DEFAULT:
          EnterpriseSettings.standardAuth();
          this.enableStandardAuth();
          break;
        case AuthMethod.LOGIN_WITH_SSO:
          EnterpriseSettings.samlSSOAuth();
          this.setState({ clientAuthMethod: AuthMethod.LOGIN_WITH_SSO });
          break;
        default:
          Logger.assert('Invalid AuthMethod: ', item.id);
      }
    }
  };

  saveStandardAuthPayload = () => {
    const { user, updateAuthMethod } = this.props;
    if (user) {
      updateAuthMethod(Tenant.getId(User.getTenant(user)), AuthMethod.DEFAULT, null, null);
    }
  };

  handleAuthLearnMoreClicked = ($event) => {
    $event.preventDefault();
    NavigationUtils.openTab(PAGE_LINK_AUTH_LEARN_MORE);
  };

  enableStandardAuth = () => {
    const { authMethod, showDialog } = this.props;
    if (authMethod.id !== AuthMethod.DEFAULT) {
      showDialog({
        title: 'You are about to switch to Standard Authentication',
        body: (
          <div>
            Users will be required to use their username/password and/or POPin's
            <br />standard magic link to log into POPin. <span
            className="link"
            onClick={ this.handleAuthLearnMoreClicked }>Learn more</span>
          </div>
        ),
        ok: 'Ok',
        cancel: 'Cancel',
        onOk: () => {
          this.setState({
            clientAuthMethod: AuthMethod.DEFAULT
          }, () => {
            this.saveStandardAuthPayload(AuthMethod.DEFAULT, null, null);
          });
        }
      });
    } else {
      this.setState({
        clientAuthMethod: AuthMethod.DEFAULT
      });
    }
  };

  constructor (props) {
    super(props);

    const { user } = this.props;
    if (!user) {
      Logger.assert('Authentication component cannot be used without a User object');
    }

    this.state = { clientAuthMethod: null };
  }

  componentDidUpdate (prevProps) {
    const { authMethod, authError } = this.props;
    handleErrorWithDialog(authError, prevProps.authError,
      'Unable to update Authentication settings');
    if (!_.isEqual(authMethod, prevProps.authMethod)) {
      this.setState({ clientAuthMethod: null });
    }
  }

  render () {
    const { clientAuthMethod } = this.state;
    const { user, authMethod } = this.props;
    return (
      <div className="card">
        <div className="row no-gutters mb-3">
          <div className="text-title my-auto text-uppercase">
            Authentication
          </div>
        </div>

        <div className="row d-flex h-100">
          <RadioGroup
            className="fix-ie"
            name="saml_method"
            selectedValue={ clientAuthMethod ? clientAuthMethod : authMethod.id }
            onChange={ this.handleAuthMethodSelected }>
            <div className="col-12">
              { getAuthMethods().map(method => {
                const tenant = User.getTenant(user);
                const samlIncomplete = method.id === AuthMethod.LOGIN_WITH_SSO &&
                  (authMethod.id === AuthMethod.DEFAULT &&
                    (clientAuthMethod === AuthMethod.LOGIN_WITH_SSO ||
                      (Tenant.getSamlIdpXmlMetadata(tenant) ||
                        Tenant.getSamlIdpMetadataUrl(tenant))));

                const warn = (method.id === AuthMethod.DEFAULT) ? false : samlIncomplete;
                return <AuthMethodRow key={ method.id } method={ method } incomplete={ warn } />;
              }) }
            </div>
          </RadioGroup>

        </div>

        <SamlSettings user={ user } clientAuthMethod={ clientAuthMethod } />
      </div>
    );
  }
}

const AuthMethodRow = ({
  method,
  incomplete
}) => {
  return (
    <div className="col mb-3" key={ method.id }>
      <div className="row align-middle align-items-center">
        <Radio className="mr-3 clickable" value={ method.id } />
        <span className="font-weight-bold">{ method.text }</span>
        { incomplete && (
          <div className="row ml-3 no-gutters incomplete-saml-configuration">
            <i className="material-icons mr-2">warning</i>
            Incomplete Configuration
          </div>
        ) }
      </div>
      <div className="col no-gutters pl-2">
        { method.desc }
      </div>
    </div>
  );
};

const getAuthMethods = () => {

  const handleLearnMoreClicked = ($event) => {
    $event.preventDefault();
    EnterpriseSettings.learnMoreSAML();
    NavigationUtils.openTab(PAGE_LINK_SAML_EXPLAIN);
  };

  return [
    {
      id: AuthMethod.DEFAULT,
      text: 'Standard Authentication',
      desc: <div className="explain">POPin standard magic link and username/password
        authentication</div>
    },
    {
      id: AuthMethod.LOGIN_WITH_SSO,
      text: 'SAML 2.0 Single Sign-On',
      desc: (
        <div className="explain">
          POPin supports Azure, Okta, and OneLogin, or your custom SAML 2.0 solution. <br />
          If you need help configuring your Single Sign-On
          integration,&nbsp; click here to &nbsp;<a onClick={ handleLearnMoreClicked } href="/#">Learn More</a>&nbsp;or contact support@popinnow.com.
        </div>
      )
    }
  ];
};

const getAuthMethod = (user) => {
  return _.chain(getAuthMethods())
    .filter(a => Tenant.getAuthMethod(User.getTenant(user)) === a.id)
    .first()
    .value();
};

const mapStateToProps = state => {
  const user = state.user.user;
  return {
    user: user,
    authentication: getAuthMethod(user),
    authError: state.authentication.error,
    authUpdating: state.authentication.loading,
    authMethod: getAuthMethod(user)
  };
};

export default connect(mapStateToProps, { updateAuthMethod, showDialog })(Authentication);
